import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Button, Form, Table, Pagination, Badge, Card, Modal, Spinner, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import '../../../assets/css/tags-input.css';
import { getCreatives } from '../../services/server-api/api';
import { useAuth } from '../../services/check_login';
import { TbFilter, TbX, TbSearch, TbEye, TbEdit, TbTrash, TbCopy, TbChevronLeft, TbChevronRight, TbChevronDown, TbClock, TbCheck } from 'react-icons/tb';
import VersionHistory from './VersionHistory';
import BulkActionsToolbar from './BulkActionsToolbar';

const CreativeTable = () => {
    const { user: currentUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [creatives, setCreatives] = useState({ creatives: [], totalPages: 0, currentPage: 1, totalItems: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedCreativeTypes, setSelectedCreativeTypes] = useState([]);
    const [selectedCreatedBy, setSelectedCreatedBy] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [allLanguages, setAllLanguages] = useState([]);
    const [languageCodeMap, setLanguageCodeMap] = useState({});
    const [allTypes, setAllTypes] = useState([]);
    const [allCreativeTypes, setAllCreativeTypes] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allStatuses, setAllStatuses] = useState([]);
    const [allTags, setAllTags] = useState([]);

    // New state for inline filters
    const [showTypeFilter, setShowTypeFilter] = useState(false);
    const [showCreativeTypeFilter, setShowCreativeTypeFilter] = useState(false);
    const [showLanguageFilter, setShowLanguageFilter] = useState(false);
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [showCreatedByFilter, setShowCreatedByFilter] = useState(false);
    const [showTagFilter, setShowTagFilter] = useState(false);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [creativeToDelete, setCreativeToDelete] = useState(null);

    // History modal state
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedCreativeForHistory, setSelectedCreativeForHistory] = useState(null);

    // Notification state
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });

    // State to track expanded languages for each creative
    const [expandedLanguages, setExpandedLanguages] = useState({});

    // State for column visibility - load from localStorage or use defaults
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem('libraryTableColumns');
        return saved ? JSON.parse(saved) : {
            title: true,
            type: true,
            creativeType: true,
            status: true,
            tags: true,
            languages: true,
            createdAt: true,
            createdBy: true,
            lastUpdated: true
        };
    });
    const [showColumnSelector, setShowColumnSelector] = useState(false);

    // Status system state
    const [statusSystemEnabled, setStatusSystemEnabled] = useState(false);

    // Bulk selection state
    const [selectedCreativeIds, setSelectedCreativeIds] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Permission checks based on creative type
    const getCreativePermissions = (creativeType) => {
        const type = creativeType?.toLowerCase();
        const permissions = currentUser?.role?.permissions || {};
        
        switch(type) {
            case 'sms':
                return {
                    canView: permissions.canViewSMS || currentUser?.isAdmin,
                    canEdit: permissions.canEditSMS || currentUser?.isAdmin,
                    canDelete: permissions.canDeleteSMS || currentUser?.isAdmin
                };
            case 'email':
                return {
                    canView: permissions.canViewEmail || currentUser?.isAdmin,
                    canEdit: permissions.canEditEmail || currentUser?.isAdmin,
                    canDelete: permissions.canDeleteEmail || currentUser?.isAdmin
                };
            case 'popup':
                return {
                    canView: permissions.canViewPopup || currentUser?.isAdmin,
                    canEdit: permissions.canEditPopup || currentUser?.isAdmin,
                    canDelete: permissions.canDeletePopup || currentUser?.isAdmin
                };
            case 'push-notification':
                return {
                    canView: permissions.canViewPushNotification || currentUser?.isAdmin,
                    canEdit: permissions.canEditPushNotification || currentUser?.isAdmin,
                    canDelete: permissions.canDeletePushNotification || currentUser?.isAdmin
                };
            default:
                return { canView: true, canEdit: false, canDelete: false };
        }
    };

    // Permission check for History
    const permissions = currentUser?.role?.permissions || {};
    const canViewHistory = permissions.canViewHistory || currentUser?.isAdmin;

    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', type: '' });
        }, 4000);
    };

    // Save column visibility to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('libraryTableColumns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    // Reset to page 1 when filters change
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [searchTerm, selectedLanguages, selectedTypes, selectedCreativeTypes, selectedCreatedBy, selectedTags, startDate, endDate]);

    // Add debounce for filters
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCreatives();
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [currentPage, searchTerm, selectedLanguages, selectedTypes, startDate, endDate, selectedCreativeTypes, selectedCreatedBy, selectedStatuses, selectedTags]);

    // Fetch filters only once on mount
    useEffect(() => {
        fetchFilters();
        fetchStatusSettings();
        fetchLanguageCodes();
    }, []);

    // Check for status filter from navigation state (from Dashboard)
    useEffect(() => {
        if (location.state?.filterByStatus) {
            setSelectedStatuses([location.state.filterByStatus]);
            // Clear the state so it doesn't persist on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
        if (location.state?.filterByCreativeType) {
            setSelectedCreativeTypes([location.state.filterByCreativeType]);
            // Clear the state so it doesn't persist on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    // Update visibleColumns when status system is enabled - only set to true if undefined (first time)
    useEffect(() => {
        if (statusSystemEnabled) {
            setVisibleColumns(prev => {
                // Only enable status column if it's undefined (first time user sees it)
                // Don't override if user explicitly set it to false
                if (prev.status === undefined) {
                    const updated = { ...prev, status: true };
                    localStorage.setItem('libraryTableColumns', JSON.stringify(updated));
                    return updated;
                }
                return prev;
            });
        }
    }, [statusSystemEnabled]);

    const fetchStatusSettings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/statuses/settings', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStatusSystemEnabled(response.data.isEnabled);
        } catch (error) {
            console.error('Error fetching status settings:', error);
        }
    };

    const fetchFilters = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Authorization token is missing');
            return;
        }

        try {
            const response = await axios.get('/library/filters', {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAllLanguages(response.data.languages);
            setAllCreativeTypes(response.data.creativeTypes);
            setAllTypes(response.data.types);
            setAllUsers(response.data.users || []);
            setAllStatuses(response.data.statuses || []);
            setAllTags(response.data.tags || []);
        } catch (error) {
            console.error('Error fetching filters:', error);
            setError('Error fetching filters');
        }
    };

    const fetchLanguageCodes = async () => {
        try {
            const response = await axios.get('/language/view');
            const map = {};
            (response.data || []).forEach(language => {
                map[language.languageName] = language.code;
            });
            setLanguageCodeMap(map);
        } catch (error) {
            console.error('Error fetching language codes:', error);
        }
    };

    const getLangLabel = (name) => languageCodeMap[name]?.toUpperCase() || name.toUpperCase();

    const fetchCreatives = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Authorization token is missing');
            setLoading(false);
            return;
        }
      
        try {
            const response = await getCreatives({
                currentPage,
                searchTerm,
                selectedLanguages,
                selectedTypes,
                selectedCreativeTypes,
                selectedCreatedBy,
                selectedStatuses,
                selectedTags,
                startDate,
                endDate,
            });
        
            

            if (response.creatives.length === 0) {
                setError('No Creatives found');
            } else {
                setError(null);
                setCreatives(response);
            }
        } catch (error) {
            setError('Error fetching creatives');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = (creative) => {
        if (creative?.availableLanguages?.length > 0) {
            navigate(`/view/${creative.availableLanguages[0]}/${creative.id}`);
        } else {
            alert('No available languages for preview');
        }
    };

    const handleEdit = (creative) => {
        if (creative?.availableLanguages?.length > 0) {
            navigate(`/creative/edit/${creative.availableLanguages[0]}/${creative.id}`);
        } else {
            alert('No available languages for preview');
        }
    };

    const handleDelete = (creative) => {
        if (!creative) return;
        setCreativeToDelete(creative);
        setShowDeleteModal(true);
    };

    const handleShowHistory = (creative) => {
        if (!creative) return;
        setSelectedCreativeForHistory(creative);
        setShowHistoryModal(true);
    };

    const confirmDelete = async () => {
        if (!creativeToDelete) return;
        
        try {
            await axios.get(`/creative/delete/${creativeToDelete.id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
    
            setCreatives(prev => ({
                ...prev,
                creatives: prev.creatives.filter(c => c.id !== creativeToDelete.id),
            }));

            showNotification('Creative deleted successfully', 'success');
            setShowDeleteModal(false);
            setCreativeToDelete(null);

        } catch (error) {
            console.error('Error deleting creative:', error);
            showNotification('Error deleting creative', 'error');
            setShowDeleteModal(false);
            setCreativeToDelete(null);
        }
    };

    const handleDuplicate = async (creative) => {
        if (!creative) return;

        try {
            await axios.get(`/creative/dublicate/${creative.id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            fetchCreatives();
        } catch (error) {
            console.error('Error duplicating creative:', error);
            alert('Error duplicating creative');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedLanguages([]);
        setSelectedTypes([]);
        setSelectedCreativeTypes([]);
        setSelectedCreatedBy([]);
        setSelectedStatuses([]);
        setSelectedTags([]);
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
    };

    // Bulk selection functions
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedCreativeIds([]);
        } else {
            setSelectedCreativeIds(creatives.creatives?.map(c => c.id) || []);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectCreative = (creativeId) => {
        setSelectedCreativeIds(prev => {
            if (prev.includes(creativeId)) {
                const newSelection = prev.filter(id => id !== creativeId);
                setSelectAll(false);
                return newSelection;
            } else {
                const newSelection = [...prev, creativeId];
                setSelectAll(newSelection.length === creatives.creatives?.length);
                return newSelection;
            }
        });
    };

    const handleBulkActionComplete = () => {
        setSelectedCreativeIds([]);
        setSelectAll(false);
        fetchCreatives();
    };

    const handlePageChange = (page) => {
        if (page === '...' || page === currentPage) return;
        setCurrentPage(page);
    };

    const toggleFilter = (type, value) => {
        if (type === 'language') {
            setSelectedLanguages(prev => 
                prev.includes(value) ? 
                prev.filter(l => l !== value) : 
                [...prev, value]
            );
        } else if (type === 'creative') {
            setSelectedTypes(prev => 
                prev.includes(value) ? 
                prev.filter(t => t !== value) : 
                [...prev, value]
            );
        } else if (type === 'creativeType') {
            setSelectedCreativeTypes(prev => 
                prev.includes(value) ? 
                prev.filter(t => t !== value) : 
                [...prev, value]
            );
        } else if (type === 'status') {
            setSelectedStatuses(prev =>
                prev.includes(value) ?
                prev.filter(s => s !== value) :
                [...prev, value]
            );
        } else if (type === 'tag') {
            setSelectedTags(prev =>
                prev.includes(value) ?
                prev.filter(t => t !== value) :
                [...prev, value]
            );
        }
    };

    const getPaginationItems = () => {
        const totalPages = creatives.totalPages;
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        if (currentPage <= 3) {
            return [1, 2, 3, '...', totalPages];
        }

        if (currentPage >= totalPages - 2) {
            return [1, '...', totalPages - 2, totalPages - 1, totalPages];
        }

        return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    };

    return (
        <Container fluid className="creative-table-container py-4 px-4">
            {/* Header Section */}
            <Row className="mb-4 align-items-center">
                <Col>
                    <h2 className="mb-0">Creative Library</h2>
                    <p className="text-muted mb-0">Manage all your creatives in one place</p>
                </Col>
            </Row>

            {/* Search Bar */}
            <Card className="mb-4 shadow-sm border-0">
                <Card.Body className="p-3 bg-light rounded">
                    <Row className="align-items-center">
                        <Col md={8}>
                            <div className="position-relative search-container">
                                <TbSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search creatives by title or tags..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5 search-input"
                                />
                            </div>
                        </Col>
                        <Col md={4} className="d-flex justify-content-end gap-2 align-items-center">
                            {/* Active filters indicator */}
                            {(selectedLanguages.length + selectedTypes.length + selectedCreativeTypes.length + selectedCreatedBy.length + selectedStatuses.length + selectedTags.length + (startDate || endDate ? 1 : 0)) > 0 && (
                                <Badge bg="primary" className="me-2">
                                    {selectedLanguages.length + selectedTypes.length + selectedCreativeTypes.length + selectedCreatedBy.length + selectedStatuses.length + selectedTags.length + (startDate || endDate ? 1 : 0)} active filter(s)
                                </Badge>
                            )}
                            {(selectedLanguages.length || selectedTypes.length || selectedCreativeTypes.length || selectedCreatedBy.length || selectedStatuses.length || selectedTags.length || startDate || endDate) && (
                                <Button variant="outline-danger" onClick={clearFilters} className="clear-btn">
                                    <TbX /> Clear All Filters
                                </Button>
                            )}
                            <Dropdown show={showColumnSelector} onToggle={() => setShowColumnSelector(!showColumnSelector)}>
                                <Dropdown.Toggle variant="outline-secondary" id="column-selector">
                                    Columns
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end" onClick={(e) => e.stopPropagation()}>
                                    {['title', 'type', 'creativeType', 'status', 'tags', 'languages', 'createdAt', 'createdBy', 'lastUpdated'].map((key) => {
                                        const value = visibleColumns[key];
                                        const columnNames = {
                                            title: 'Title',
                                            type: 'Creative',
                                            creativeType: 'Type',
                                            status: 'Status',
                                            tags: 'Tags',
                                            languages: 'Languages',
                                            createdAt: 'Created At',
                                            createdBy: 'Created By',
                                            lastUpdated: 'Last Updated'
                                        };
                                        
                                        // Hide status column option if status system is disabled
                                        if (key === 'status' && !statusSystemEnabled) {
                                            return null;
                                        }
                                        
                                        return (
                                            <Dropdown.Item
                                                key={key}
                                                onClick={() => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }))}
                                                className="d-flex justify-content-between align-items-center"
                                            >
                                                <span>{columnNames[key]}</span>
                                                {value && <TbCheck className="text-primary" />}
                                            </Dropdown.Item>
                                        );
                                    })}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Main Content */}
            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    {/* Bulk Actions Toolbar */}
                    <BulkActionsToolbar 
                        selectedCreatives={selectedCreativeIds}
                        onActionComplete={handleBulkActionComplete}
                        allStatuses={allStatuses}
                        statusSystemEnabled={statusSystemEnabled}
                    />
                    {loading ? (
                        <div className="d-flex justify-content-center py-5">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : error ? (
                        <div className="text-center py-5">
                            <div className="mb-3">
                                <TbSearch size={48} className="text-muted" />
                            </div>
                            <h5 className="text-muted mb-2">{error}</h5>
                            <p className="text-muted">
                                {searchTerm || selectedLanguages.length || selectedTypes.length || selectedCreativeTypes.length || selectedCreatedBy.length || selectedTags.length || startDate || endDate
                                    ? 'Try adjusting your search criteria or filters.'
                                    : 'No creatives have been created yet.'}
            </p>
                            {(searchTerm || selectedLanguages.length || selectedTypes.length || selectedCreativeTypes.length || selectedCreatedBy.length || selectedTags.length || startDate || endDate) && (
                                <Button variant="outline-primary" onClick={clearFilters} className="mt-2">
                                    Clear All Filters
                                </Button>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '50px' }}>
                                                <Form.Check 
                                                    type="checkbox"
                                                    checked={selectAll}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            {visibleColumns.title && (
                                            <th style={{ width: '25%' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>Title</span>
                                                    <div className="filter-icon-container">
                                                        <TbSearch size={16} className="text-secondary" />
                                                    </div>
                                                </div>
                                            </th>
                                            )}
                                            {visibleColumns.type && (
                                            <th style={{ width: '15%' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>Creative</span>
                                                    <div className="filter-icon-container">
                                                        <Dropdown show={showTypeFilter} onToggle={() => setShowTypeFilter(!showTypeFilter)}>
                                                            <Dropdown.Toggle 
                                                                variant="link" 
                                                                className="p-0 border-0 text-secondary column-filter-toggle d-flex align-items-center"
                                                                style={{ background: 'none', boxShadow: 'none' }}
                                                            >
                                                                <TbFilter size={16} className={selectedTypes.length ? 'text-warning' : 'text-secondary'} />
                                                                {selectedTypes.length > 0 && (
                                                                    <Badge bg="warning" className="ms-1" style={{fontSize: '9px'}}>{selectedTypes.length}</Badge>
                                                                )}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="column-filter-menu">
                                                                {allTypes.map(type => (
                                                                    <Dropdown.Item 
                                                                        key={type} 
                                                                        as="div"
                                                                        className="d-flex align-items-center justify-content-between filter-item"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            toggleFilter('creative', type);
                                                                        }}
                                                                    >
                                                                        <span>{type}</span>
                                                                        {selectedTypes.includes(type) && <TbCheck className="text-primary" />}
                                                                    </Dropdown.Item>
                                                                ))}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </div>
                                            </th>
                                            )}
                                            {visibleColumns.creativeType && (
                                            <th style={{ width: '15%' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>Type</span>
                                                    <div className="filter-icon-container">
                                                        <Dropdown show={showCreativeTypeFilter} onToggle={() => setShowCreativeTypeFilter(!showCreativeTypeFilter)}>
                                                            <Dropdown.Toggle 
                                                                variant="link" 
                                                                className="p-0 border-0 text-secondary column-filter-toggle d-flex align-items-center"
                                                                style={{ background: 'none', boxShadow: 'none' }}
                                                            >
                                                                <TbFilter size={16} className={selectedCreativeTypes.length ? 'text-warning' : 'text-secondary'} />
                                                                {selectedCreativeTypes.length > 0 && (
                                                                    <Badge bg="warning" className="ms-1" style={{fontSize: '9px'}}>{selectedCreativeTypes.length}</Badge>
                                                                )}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="column-filter-menu">
                                                                {allCreativeTypes.map(type => (
                                                                    <Dropdown.Item 
                                                                        key={type} 
                                                                        as="div"
                                                                        className="d-flex align-items-center justify-content-between filter-item"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            toggleFilter('creativeType', type);
                                                                        }}
                                                                    >
                                                                        <span>{type}</span>
                                                                        {selectedCreativeTypes.includes(type) && <TbCheck className="text-primary" />}
                                                                    </Dropdown.Item>
                                                                ))}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </div>
                                            </th>
                                            )}
                                            {statusSystemEnabled && visibleColumns.status && (
                                            <th style={{ width: '12%' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>Status</span>
                                                    <div className="filter-icon-container">
                                                        <Dropdown show={showStatusFilter} onToggle={() => setShowStatusFilter(!showStatusFilter)}>
                                                            <Dropdown.Toggle 
                                                                variant="link" 
                                                                className="p-0 border-0 text-secondary column-filter-toggle d-flex align-items-center"
                                                                style={{ background: 'none', boxShadow: 'none' }}
                                                            >
                                                                <TbFilter size={16} className={selectedStatuses.length ? 'text-warning' : 'text-secondary'} />
                                                                {selectedStatuses.length > 0 && (
                                                                    <Badge bg="warning" className="ms-1" style={{fontSize: '9px'}}>{selectedStatuses.length}</Badge>
                                                                )}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="column-filter-menu">
                                                                {allStatuses.map(status => (
                                                                    <Dropdown.Item 
                                                                        key={status._id} 
                                                                        as="div"
                                                                        className="d-flex align-items-center justify-content-between filter-item"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            toggleFilter('status', status._id);
                                                                        }}
                                                                    >
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <div 
                                                                                style={{
                                                                                    width: '12px',
                                                                                    height: '12px',
                                                                                    borderRadius: '2px',
                                                                                    backgroundColor: status.color
                                                                                }}
                                                                            />
                                                                            <span>{status.name}</span>
                                                                        </div>
                                                                        {selectedStatuses.includes(status._id) && <TbCheck className="text-primary" />}
                                                                    </Dropdown.Item>
                                                                ))}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </div>
                                            </th>
                                            )}
                                            {visibleColumns.tags && (
                                            <th style={{ width: '12%' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>Tags</span>
                                                    <div className="filter-icon-container">
                                                        <Dropdown show={showTagFilter} onToggle={() => setShowTagFilter(!showTagFilter)}>
                                                            <Dropdown.Toggle
                                                                variant="link"
                                                                className="p-0 border-0 text-secondary column-filter-toggle d-flex align-items-center"
                                                                style={{ background: 'none', boxShadow: 'none' }}
                                                            >
                                                                <TbFilter size={16} className={selectedTags.length ? 'text-warning' : 'text-secondary'} />
                                                                {selectedTags.length > 0 && (
                                                                    <Badge bg="warning" className="ms-1" style={{fontSize: '9px'}}>{selectedTags.length}</Badge>
                                                                )}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="column-filter-menu">
                                                                {allTags.length > 0 ? allTags.map(tag => (
                                                                    <Dropdown.Item
                                                                        key={tag}
                                                                        as="div"
                                                                        className="d-flex align-items-center justify-content-between filter-item"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            toggleFilter('tag', tag);
                                                                        }}
                                                                    >
                                                                        <span>{tag}</span>
                                                                        {selectedTags.includes(tag) && <TbCheck className="text-primary" />}
                                                                    </Dropdown.Item>
                                                                )) : (
                                                                    <Dropdown.Item disabled className="text-muted small">No tags yet</Dropdown.Item>
                                                                )}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </div>
                                            </th>
                                            )}
                                            {visibleColumns.languages && (
                                            <th style={{ width: '15%' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>Languages</span>
                                                    <div className="filter-icon-container">
                                                        <Dropdown show={showLanguageFilter} onToggle={() => setShowLanguageFilter(!showLanguageFilter)}>
                                                            <Dropdown.Toggle 
                                                                variant="link" 
                                                                className="p-0 border-0 text-secondary column-filter-toggle d-flex align-items-center"
                                                                style={{ background: 'none', boxShadow: 'none' }}
                                                            >
                                                                <TbFilter size={16} className={selectedLanguages.length ? 'text-warning' : 'text-secondary'} />
                                                                {selectedLanguages.length > 0 && (
                                                                    <Badge bg="warning" className="ms-1" style={{fontSize: '9px'}}>{selectedLanguages.length}</Badge>
                                                                )}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="column-filter-menu">
                                                                {allLanguages.map(lang => (
                                                                    <Dropdown.Item 
                                                                        key={lang} 
                                                                        as="div"
                                                                        className="d-flex align-items-center justify-content-between text-uppercase filter-item"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            toggleFilter('language', lang);
                                                                        }}
                                                                    >
                                                                        <span>{getLangLabel(lang)}</span>
                                                                        {selectedLanguages.includes(lang) && <TbCheck className="text-primary" />}
                                                                    </Dropdown.Item>
                                                                ))}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </div>
                                            </th>
                                            )}
                                            {visibleColumns.createdAt && (
                                            <th style={{ width: '15%' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>Created At</span>
                                                    <div className="filter-icon-container">
                                                        <Dropdown show={showDateFilter} onToggle={() => setShowDateFilter(!showDateFilter)}>
                                                            <Dropdown.Toggle 
                                                                variant="link" 
                                                                className="p-0 border-0 text-secondary column-filter-toggle d-flex align-items-center"
                                                                style={{ background: 'none', boxShadow: 'none' }}
                                                            >
                                                                <TbFilter size={16} className={(startDate || endDate) ? 'text-warning' : 'text-secondary'} />
                                                                {(startDate || endDate) && (
                                                                    <Badge bg="warning" className="ms-1" style={{fontSize: '9px'}}>1</Badge>
                                                                )}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="column-filter-menu p-3 date-filter-menu" onClick={(e) => e.stopPropagation()}>
                                                                <div className="mb-2">
                                                                    <label className="form-label small fw-semibold">From:</label>
                                                                    <DatePicker
                                                                        selected={startDate}
                                                                        onChange={date => setStartDate(date)}
                                                                        selectsStart
                                                                        startDate={startDate}
                                                                        endDate={endDate}
                                                                        placeholderText="Start Date"
                                                                        className="form-control form-control-sm"
                                                                        dateFormat="dd/MM/yyyy"
                                                                        showMonthDropdown
                                                                        showYearDropdown
                                                                        dropdownMode="select"
                                                                        autoComplete="off"
                                                                        withPortal={false}
                                                                        popperPlacement="bottom-start"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="form-label small fw-semibold">To:</label>
                                                                    <DatePicker
                                                                        selected={endDate}
                                                                        onChange={date => setEndDate(date)}
                                                                        selectsEnd
                                                                        startDate={startDate}
                                                                        endDate={endDate}
                                                                        minDate={startDate}
                                                                        placeholderText="End Date"
                                                                        className="form-control form-control-sm"
                                                                        dateFormat="dd/MM/yyyy"
                                                                        showMonthDropdown
                                                                        showYearDropdown
                                                                        dropdownMode="select"
                                                                        autoComplete="off"
                                                                        withPortal={false}
                                                                        popperPlacement="bottom-start"
                                                                    />
                                                                </div>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </div>
                                            </th>
                                            )}
                                            {visibleColumns.createdBy && (
                                            <th style={{ width: '15%' }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span>Created By</span>
                                                    <div className="filter-icon-container">
                                                        <Dropdown show={showCreatedByFilter} onToggle={() => setShowCreatedByFilter(!showCreatedByFilter)}>
                                                            <Dropdown.Toggle 
                                                                variant="link" 
                                                                className="p-0 border-0 text-secondary column-filter-toggle d-flex align-items-center"
                                                                style={{ background: 'none', boxShadow: 'none' }}
                                                            >
                                                                <TbFilter size={16} className={selectedCreatedBy.length > 0 ? 'text-warning' : 'text-secondary'} />
                                                                {selectedCreatedBy.length > 0 && (
                                                                    <Badge bg="warning" className="ms-1" style={{fontSize: '9px'}}>{selectedCreatedBy.length}</Badge>
                                                                )}
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="column-filter-menu p-2" onClick={(e) => e.stopPropagation()}>
                                                                {allUsers.map((user) => (
                                                                    <Dropdown.Item 
                                                                        key={user.username}
                                                                        onClick={() => {
                                                                            setSelectedCreatedBy(prev =>
                                                                                prev.includes(user.username)
                                                                                    ? prev.filter(u => u !== user.username)
                                                                                    : [...prev, user.username]
                                                                            );
                                                                        }}
                                                                        className="d-flex justify-content-between align-items-center filter-option"
                                                                    >
                                                                        <span>{user.fullName}</span>
                                                                        {selectedCreatedBy.includes(user.username) && <TbCheck className="text-primary" />}
                                                                    </Dropdown.Item>
                                                                ))}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </div>
                                            </th>
                                            )}
                                            {visibleColumns.lastUpdated && (
                                            <th style={{ width: '15%' }}>
                                                <span>Last Updated</span>
                                            </th>
                                            )}
                                            <th className="text-center" style={{ width: '15%' }}>
                                                <span>Actions</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {creatives.creatives?.map((creative) => (
                                            <tr key={creative.id} className="table-row">
                                                <td>
                                                    <Form.Check 
                                                        type="checkbox"
                                                        checked={selectedCreativeIds.includes(creative.id)}
                                                        onChange={() => handleSelectCreative(creative.id)}
                                                    />
                                                </td>
                                                {visibleColumns.title && (
                                                <td className="fw-semibold">
                                                    <div>{creative.title}</div>
                                                </td>
                                                )}
                                                {visibleColumns.type && (
                                                <td><span className={`text-uppercase type-badge activity-type ${creative.creative?.toLowerCase() || 'default'}`}>{creative.creative || 'N/A'}</span></td>
                                                )}
                                                {visibleColumns.creativeType && (
                                                <td><Badge bg="secondary" className="creative-type-badge">{creative.creativeType}</Badge></td>
                                                )}
                                                {statusSystemEnabled && visibleColumns.status && (
                                                <td>
                                                    {creative.status ? (
                                                        <span 
                                                            className="badge status-badge"
                                                            style={{ 
                                                                '--status-color': creative.status.color,
                                                                backgroundColor: creative.status.color,
                                                                color: '#fff',
                                                                fontWeight: '600'
                                                            }}
                                                        >
                                                            {creative.status.name}
                                                        </span>
                                                    ) : (
                                                        <span className="small text-muted">No Status</span>
                                                    )}
                                                </td>
                                                )}
                                                {visibleColumns.tags && (
                                                <td>
                                                    {creative.tags?.length > 0 ? (
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {creative.tags.map((tag, index) => (
                                                                <span key={index} className="tag-chip tag-chip-sm">
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted small">—</span>
                                                    )}
                                                </td>
                                                )}
                                                {visibleColumns.languages && (
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {creative.availableLanguages?.slice(0, expandedLanguages[creative.id] ? creative.availableLanguages.length : 2).map((lang, index) => (
                                                            <Badge key={index} bg="light" text="dark" className="text-uppercase language-badge">
                                                                {getLangLabel(lang)}
                                                            </Badge>
                                                        ))}
                                                        {creative.availableLanguages?.length > 2 && (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="p-0 text-primary"
                                                                style={{ fontSize: '0.75rem', textDecoration: 'none', minWidth: 'auto' }}
                                                                onClick={() => setExpandedLanguages(prev => ({
                                                                    ...prev,
                                                                    [creative.id]: !prev[creative.id]
                                                                }))}
                                                            >
                                                                {expandedLanguages[creative.id] ? 'Less' : 'More'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                                )}
                                                {visibleColumns.createdAt && (
                                                <td className="text-muted">
                                                    <span className="small">{new Date(creative.createdAt).toLocaleDateString()} {new Date(creative.createdAt).toLocaleTimeString()}</span>
                                                </td>
                                                )}
                                                {visibleColumns.createdBy && (
                                                <td className="text-muted">
                                                    <span className="small">{creative.createdByName || creative.user || 'N/A'}</span>
                                                </td>
                                                )}
                                                {visibleColumns.lastUpdated && (
                                                <td className="text-muted">
                                                    {creative.updatedAt && creative.updatedAt !== creative.createdAt ? (
                                                        <div>
                                                            <div className="small">{creative.updatedByName || creative.userUpdate || creative.user || 'N/A'}</div>
                                                            <div className="small">{new Date(creative.updatedAt).toLocaleDateString()} {new Date(creative.updatedAt).toLocaleTimeString()}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="small text-muted">Not updated</span>
                                                    )}
                                                </td>
                                                )}
                                                <td>
                                                    <div className="d-flex justify-content-center gap-2 action-icons">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Preview</Tooltip>}>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm" 
                                                                className="action-icon-btn"
                                                                onClick={() => handlePreview(creative)}
                                                            >
                                                                <TbEye size={16} />
                                                            </Button>
                                                        </OverlayTrigger>
                                                        
                                                        {canViewHistory && (
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>History</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-warning" 
                                                                    size="sm" 
                                                                    className="action-icon-btn"
                                                                    onClick={() => handleShowHistory(creative)}
                                                                >
                                                                    <TbClock size={16} />
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}

                                                        {getCreativePermissions(creative.creative).canEdit && (
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-secondary" 
                                                                    size="sm" 
                                                                    className="action-icon-btn"
                                                                    onClick={() => handleEdit(creative)}
                                                                >
                                                                    <TbEdit size={16} />
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}

                                                        {getCreativePermissions(creative.creative).canEdit && (
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Duplicate</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm" 
                                                                    className="action-icon-btn"
                                                                    onClick={() => handleDuplicate(creative)}
                                                                >
                                                                    <TbCopy size={16} />
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}

                                                        {getCreativePermissions(creative.creative).canDelete && (
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-danger" 
                                                                    size="sm" 
                                                                    className="action-icon-btn"
                                                                    onClick={() => handleDelete(creative)}
                                                                >
                                                                    <TbTrash size={16} />
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {creatives.totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted">
                                        Showing page {currentPage} of {creatives.totalPages}
                                    </div>
                                    <Pagination className="mb-0 custom-pagination">
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)} 
                                            disabled={currentPage === 1}
                                            className="page-nav"
                                        >
                                            <TbChevronLeft />
                                        </Pagination.Prev>
                                        
                                        {getPaginationItems().map((item, idx) =>
                                            item === '...' ? (
                                                <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled className="page-dots" />
                                            ) : (
                                                <Pagination.Item
                                                    key={`page-${item}`}
                                                    active={item === currentPage}
                                                    onClick={() => handlePageChange(item)}
                                                    className="page-item"
                                                >
                                                    {item}
                                                </Pagination.Item>
                                            )
                                        )}
                                        
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)} 
                                            disabled={currentPage === creatives.totalPages}
                                            className="page-nav"
                                        >
                                            <TbChevronRight />
                                        </Pagination.Next>
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-3">
                        Are you sure that want to delete: <strong>{creativeToDelete?.title}</strong>?
                    </p>

                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => {
                            setShowDeleteModal(false);
                            setCreativeToDelete(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={confirmDelete}
                    >
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Version History Modal */}
            {showHistoryModal && selectedCreativeForHistory && (
                <VersionHistory
                    creativeId={selectedCreativeForHistory.id}
                    creativeType={selectedCreativeForHistory.creative}
                    onClose={() => {
                        setShowHistoryModal(false);
                        setSelectedCreativeForHistory(null);
                    }}
                    onRestore={() => {
                        setShowHistoryModal(false);
                        setSelectedCreativeForHistory(null);
                        fetchCreatives();
                    }}
                />
            )}

            {/* Notification */}
            {notification.show && (
                <div
                    className={`notification ${notification.type}`}
                    style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        padding: '15px 25px',
                        borderRadius: '8px',
                        backgroundColor: notification.type === 'success' ? '#4CAF50' : '#f44336',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 10000,
                        fontSize: '14px',
                        fontWeight: '500',
                        animation: 'slideInRight 0.3s ease-out'
                    }}
                >
                    {notification.message}
                </div>
            )}
        </Container>
    );
};

export default CreativeTable;